import React, { useState, useRef, useCallback } from 'react';
import { X, FileText, BarChart2, Loader2, Download, Copy, CheckCircle2 } from 'lucide-react';
import { streamExecutiveReport, streamPerformanceReport } from '../lib/api';
import { Button } from './ui/button';
import DOMPurify from 'dompurify';

const REPORT_TYPES = [
  {
    id: 'executive',
    label: 'Resumen Ejecutivo',
    description: 'Estado general de incidencias por zona, prioridad y tipo',
    icon: FileText,
    fn: streamExecutiveReport,
  },
  {
    id: 'performance',
    label: 'Rendimiento del Equipo',
    description: 'Tasas de resolución y productividad por encargado',
    icon: BarChart2,
    fn: streamPerformanceReport,
  },
];

export default function AIReportModal({ onClose }) {
  const [selectedType, setSelectedType] = useState(null);
  const [streaming, setStreaming] = useState(false);
  const [content, setContent] = useState('');
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const abortRef = useRef(null);
  const contentRef = useRef('');

  const startReport = useCallback((type) => {
    setSelectedType(type);
    setStreaming(true);
    setContent('');
    setDone(false);
    setError('');
    contentRef.current = '';

    abortRef.current = type.fn(
      {},
      (chunk) => {
        contentRef.current += chunk;
        setContent(contentRef.current);
      },
      () => { setStreaming(false); setDone(true); },
      (err) => { setStreaming(false); setError(err.message); }
    );
  }, []);

  const handleStop = () => {
    abortRef.current?.abort();
    setStreaming(false);
  };

  const handleCopy = async () => {
    await navigator.clipboard.writeText(content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownload = () => {
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `informe_${selectedType?.id}_${new Date().toISOString().slice(0,10)}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const renderMarkdown = (text) => {
    const raw = text
      .replace(/^### (.+)$/gm, '<h3 class="text-base font-bold mt-4 mb-1 text-gray-800 dark:text-gray-100">$1</h3>')
      .replace(/^## (.+)$/gm, '<h2 class="text-lg font-bold mt-5 mb-2 text-gray-900 dark:text-white">$1</h2>')
      .replace(/^# (.+)$/gm, '<h1 class="text-xl font-bold mt-6 mb-2 text-gray-900 dark:text-white">$1</h1>')
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.+?)\*/g, '<em>$1</em>')
      .replace(/^- (.+)$/gm, '<li class="ml-4 list-disc">$1</li>')
      .replace(/^(\d+)\. (.+)$/gm, '<li class="ml-4 list-decimal">$2</li>')
      .replace(/\n\n/g, '<br/><br/>');
    return DOMPurify.sanitize(raw, {
      ALLOWED_TAGS: ['h1','h2','h3','strong','em','li','br','p','span'],
      ALLOWED_ATTR: ['class'],
    });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-3xl mx-4 flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700 shrink-0">
          <div>
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">Informes con IA</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400">Generado por Claude Sonnet — datos en tiempo real</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Report type selection */}
        {!selectedType && (
          <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {REPORT_TYPES.map((type) => (
              <button
                key={type.id}
                onClick={() => startReport(type)}
                data-testid={`report-type-${type.id}`}
                className="group text-left p-5 rounded-xl border-2 border-gray-200 dark:border-gray-700 hover:border-[#008040] hover:bg-green-50 dark:hover:bg-green-900/10 transition-all"
              >
                <div className="flex items-start gap-3">
                  <div className="bg-[#008040]/10 group-hover:bg-[#008040]/20 rounded-lg p-2 transition-colors">
                    <type.icon className="w-6 h-6 text-[#008040]" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 dark:text-white text-sm">{type.label}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{type.description}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Streaming content */}
        {selectedType && (
          <>
            <div className="flex items-center justify-between px-6 py-3 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 shrink-0">
              <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                {streaming ? (
                  <><Loader2 className="w-4 h-4 animate-spin text-[#008040]" /> Generando {selectedType.label}...</>
                ) : done ? (
                  <><CheckCircle2 className="w-4 h-4 text-[#008040]" /> Informe completo</>
                ) : null}
                {error && <span className="text-red-500">Error: {error}</span>}
              </div>
              <div className="flex items-center gap-2">
                {done && (
                  <>
                    <button onClick={handleCopy} className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-200 px-2 py-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors" data-testid="report-copy">
                      {copied ? <CheckCircle2 className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
                      {copied ? 'Copiado' : 'Copiar'}
                    </button>
                    <button onClick={handleDownload} className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-200 px-2 py-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors" data-testid="report-download">
                      <Download className="w-3.5 h-3.5" /> Descargar
                    </button>
                  </>
                )}
                {streaming && (
                  <button onClick={handleStop} className="text-xs text-red-500 hover:text-red-700 px-2 py-1 rounded hover:bg-red-50 transition-colors">
                    Detener
                  </button>
                )}
                <button onClick={() => { setSelectedType(null); setContent(''); setDone(false); setError(''); }} className="text-xs text-gray-400 hover:text-gray-600 transition-colors">
                  ← Volver
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6 text-sm text-gray-800 dark:text-gray-200 leading-relaxed" data-testid="report-content">
              {content ? (
                <div dangerouslySetInnerHTML={{ __html: renderMarkdown(content) }} />
              ) : (
                <div className="flex items-center justify-center h-32 text-gray-400">
                  <Loader2 className="w-6 h-6 animate-spin mr-2" /> Iniciando generación...
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
